import { LitElement, html, css, nothing } from "lit";
import { customElement, property } from "lit/decorators.js";
import { localized, msg } from "@lit/localize";
import type { startseite } from "@progressive-development/politik-model";

import { navigateOnClick } from "../../../../../common/router/app-navigation.js";
import { blockStyles } from "../../../../../common/styles/block-styles.js";
import { auswahlZeile } from "../../../../../common/components/block-parts.js";
import { ROUTE_AUSSCHUESSE, ROUTE_GREMIEN } from "../../../../../config/navigation.js";
import type { GebietsAuswahl } from "../../../../../config/gebiet-auswahl.js";
import { organigrammZahlen } from "../../../../gremien/gremien-uebersicht-daten.js";
import type { EigeneBv } from "../../../../gremien/sections/app-organigramm.js";
import { AUSWAHL_GREMIEN, AUSWAHL_GREMIEN_GEBIET } from "./auswahl-texte.js";

import "../../../../gremien/sections/app-organigramm.js";

const TYP_BEZIRKSVERTRETUNG = "bezirksvertretung";

/**
 * Block 3 der Startseite — „Wer entscheidet": die KURZFORM des Organigramms
 * (FACHKONZEPT-gremien-uebersicht.md, 3). Links „Gewählt" (Rat als Balken in
 * Parteifarben, meine Bezirksvertretung mit Merker), rechts „Verwaltung"
 * (OB als Scharnier, Dezernate, Beschäftigte, Datenton). Dasselbe Bild wie
 * auf `/gremien`, nur kleiner — dorthin führt der Fuß.
 *
 * Der Merker filtert diesen Block nicht, er wählt die eigene BV aus. Alles
 * andere gilt stadtweit. Die Landkarte (Ausschuss-Gliederung, Fraktionen,
 * Erklärungen) liegt auf der Übersicht, nicht hier.
 */
@customElement("app-gremien-block")
@localized()
export class AppGremienBlock extends LitElement {
  @property({ attribute: false }) gremien: startseite.GremiumEintrag[] = [];

  @property({ attribute: false }) sitzverteilung?: startseite.SitzverteilungDokument;

  @property({ attribute: false }) parteien: startseite.ParteiEintrag[] = [];

  @property({ attribute: false }) gremienDerStadt?: startseite.GremienDerStadtDokument;

  @property({ attribute: false }) dezernate?: startseite.DezernateDokument;

  /** Die nächste Sitzung der BV des gemerkten Bezirks — aus dem Aufmacher. */
  @property({ attribute: false }) naechsteBvSitzung?: startseite.TerminEintrag;

  @property({ attribute: false }) merker?: GebietsAuswahl;

  static override styles = [
    blockStyles,
    css`
      app-organigramm {
        display: block;
        margin-top: var(--portal-space-3);
      }
      .fuss {
        display: flex;
        flex-wrap: wrap;
        gap: var(--portal-space-2) var(--portal-space-6);
        margin: var(--portal-space-4) 0 0;
        padding-top: var(--portal-space-3);
        border-top: 1px solid var(--portal-linie);
        list-style: none;
        padding-left: 0;
      }
      .fuss a {
        color: inherit;
      }
    `,
  ];

  /** Die BV des gemerkten Bezirks — Zuordnung über den Slug, nie über den Namen. */
  private get _eigeneBv(): EigeneBv | undefined {
    const bezirk = this.merker?.bezirkSlug;
    if (!bezirk) return undefined;
    const bv = this.gremien.find((g) => g.typ === TYP_BEZIRKSVERTRETUNG && g.bezirk === bezirk);
    if (!bv) return undefined;
    return {
      name: bv.name,
      bezirk,
      vorsitz: this.sitzverteilung?.bezirksbuergermeister?.find((b) => b.bezirk === bezirk)?.name,
      naechsteSitzung: this.naechsteBvSitzung,
    };
  }

  override render() {
    const zahlen = organigrammZahlen({
      gremien: this.gremien,
      sitzverteilung: this.sitzverteilung,
      gremienDerStadt: this.gremienDerStadt,
      dezernate: this.dezernate,
    });
    if (zahlen.ratSitze === undefined && zahlen.bezirksvertretungen === undefined) return nothing;
    const wege = [
      {
        pfad: `/${ROUTE_AUSSCHUESSE}`,
        text: zahlen.ausschuesse
          ? `${zahlen.ausschuesse} ${msg("Ausschüsse und Gremien", { id: "app.start.gremien.ausschuesseGremien" })}`
          : msg("Ausschüsse und Gremien", { id: "app.start.gremien.ausschuesseGremien" }),
      },
      {
        pfad: `/${ROUTE_GREMIEN}`,
        text: msg("Wer entscheidet in Köln — die Übersicht", {
          id: "app.start.gremien.uebersicht",
        }),
      },
    ];
    return html`
      <h2>${msg("Wer entscheidet", { id: "app.start.gremien.titel" })}</h2>
      ${auswahlZeile(this.merker ? AUSWAHL_GREMIEN_GEBIET() : AUSWAHL_GREMIEN())}
      <app-organigramm
        kompakt
        .zahlen=${zahlen}
        .sitzverteilung=${this.sitzverteilung}
        .parteien=${this.parteien}
        .eigeneBv=${this._eigeneBv}
      ></app-organigramm>
      <ul class="fuss">
        ${wege.map(
          (w) =>
            html`<li>
              <a class="weiter" href=${w.pfad} @click=${(e: Event) => navigateOnClick(e, w.pfad)}
                >${w.text} →</a
              >
            </li>`,
        )}
      </ul>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "app-gremien-block": AppGremienBlock;
  }
}
